/**
 * CustomLicenceWizard — Tunnel ADM "Nouvelle licence custom" pour clients gros volume.
 * Crée en une transaction atomique : licence + simulator_licences + grant mutualisé.
 * Évite le piège "N grants distincts = N pools séparés au lieu d'un pool mutualisé".
 */

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { MultiSelect } from "./ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, Check, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { adminApi } from "../lib/api";

interface ClientAccount {
  id: string;
  name: string;
}

interface SimulatorOption {
  id: string;
  name: string;
  isActive?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  clientAccounts: ClientAccount[];
  simulators: SimulatorOption[];
}

type Step = 1 | 2 | 3;

function generateCode(accountName: string | null | undefined): string {
  const slug = (accountName || "CLIENT")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 20) || "CLIENT";
  const now = new Date();
  const yyyymmdd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const rand = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4).padEnd(4, "X");
  return `CUSTOM_${slug}_${yyyymmdd}_${rand}`;
}

export function CustomLicenceWizard({ open, onClose, onCreated, clientAccounts, simulators }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Étape 1 — Identité licence
  const [clientAccountId, setClientAccountId] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [zohoQuoteRef, setZohoQuoteRef] = useState<string>("");

  // Étape 2 — Périmètre simulateurs
  const [simulatorIds, setSimulatorIds] = useState<string[]>([]);

  // Étape 3 — Attribution + quota
  const [quantity, setQuantity] = useState<number>(1);
  const [rateLimitSessions, setRateLimitSessions] = useState<number>(100);
  const [durationMonths, setDurationMonths] = useState<string>(""); // vide = permanent
  const [note, setNote] = useState<string>("");

  const selectedAccount = useMemo(() => clientAccounts.find((a) => a.id === clientAccountId) || null, [clientAccounts, clientAccountId]);

  // Auto-suggest code + name quand le compte change (sauf si l'utilisateur a déjà saisi manuellement)
  useEffect(() => {
    if (!selectedAccount) return;
    if (!codeManuallyEdited) setCode(generateCode(selectedAccount.name));
    if (!name) setName(`${selectedAccount.name} — Custom`);
  }, [selectedAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset à l'ouverture
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSubmitting(false);
    setError(null);
    setClientAccountId("");
    setCode("");
    setCodeManuallyEdited(false);
    setName("");
    setDescription("");
    setZohoQuoteRef("");
    setSimulatorIds([]);
    setQuantity(1);
    setRateLimitSessions(100);
    setDurationMonths("");
    setNote("");
  }, [open]);

  const activeSimulators = useMemo(() => simulators.filter((s) => s.isActive !== false), [simulators]);

  const simulatorLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of activeSimulators) {
      map.set(s.id, s.name);
    }
    return map;
  }, [activeSimulators]);

  const canGoStep2 = !!clientAccountId && code.trim().length > 0 && name.trim().length > 0;
  const canGoStep3 = simulatorIds.length > 0;
  const canSubmit = canGoStep2 && canGoStep3 && quantity >= 1 && rateLimitSessions >= 1;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const noteParts: string[] = [];
      if (zohoQuoteRef.trim()) noteParts.push(`Devis Zoho : ${zohoQuoteRef.trim()}`);
      if (note.trim()) noteParts.push(note.trim());

      const payload = {
        licence: {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          color: null as string | null,
        },
        simulatorIds,
        grant: {
          clientAccountId,
          quantity,
          durationMonths: durationMonths ? Number(durationMonths) : null,
          rateLimitMode: "one_shot" as const,
          rateLimitScope: "per_account" as const,
          rateLimitSessions,
          note: noteParts.join(" — ") || null,
        },
      };

      await adminApi.createCustomLicence(payload);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  const stepperLabels: Record<Step, string> = {
    1: t("customLicenceWizard.step1Title"),
    2: t("customLicenceWizard.step2Title"),
    3: t("customLicenceWizard.step3Title"),
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-violet-600" />
            {t("customLicenceWizard.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-1 pb-2">
          {([1, 2, 3] as Step[]).map((s) => (
            <React.Fragment key={s}>
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition ${
                  step === s ? "bg-violet-600 text-white" : step > s ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}
                aria-current={step === s ? "step" : undefined}
              >
                {step > s ? <Check className="h-4 w-4" /> : s}
              </div>
              <div className="text-xs text-gray-700 mr-2">{stepperLabels[s]}</div>
              {s < 3 && <div className={`h-px flex-1 ${step > s ? "bg-green-300" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 — Identité */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="clw-account">{t("customLicenceWizard.fieldAccount")} *</Label>
              <Select value={clientAccountId} onValueChange={setClientAccountId}>
                <SelectTrigger id="clw-account">
                  <SelectValue placeholder={t("customLicenceWizard.fieldAccountPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {clientAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="clw-code">{t("customLicenceWizard.fieldCode")} *</Label>
              <Input
                id="clw-code"
                value={code}
                onChange={(e) => { setCode(e.target.value); setCodeManuallyEdited(true); }}
                placeholder={t("customLicenceWizard.fieldCodePlaceholder")}
              />
              <p className="text-xs text-gray-500 mt-1">{t("customLicenceWizard.fieldCodeHelp")}</p>
            </div>

            <div>
              <Label htmlFor="clw-name">{t("customLicenceWizard.fieldName")} *</Label>
              <Input
                id="clw-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("customLicenceWizard.fieldNamePlaceholder")}
              />
            </div>

            <div>
              <Label htmlFor="clw-description">{t("customLicenceWizard.fieldDescription")}</Label>
              <Textarea
                id="clw-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("customLicenceWizard.fieldDescriptionPlaceholder")}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="clw-zoho">{t("customLicenceWizard.fieldZohoRef")}</Label>
              <Input
                id="clw-zoho"
                value={zohoQuoteRef}
                onChange={(e) => setZohoQuoteRef(e.target.value)}
                placeholder={t("customLicenceWizard.fieldZohoRefPlaceholder")}
              />
            </div>
          </div>
        )}

        {/* Step 2 — Périmètre simulateurs */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label>{t("customLicenceWizard.fieldSimulators")} *</Label>
              <MultiSelect
                options={activeSimulators.map((s) => s.id)}
                value={simulatorIds}
                onChange={setSimulatorIds}
                getLabel={(id) => simulatorLabelMap.get(id) || id}
                placeholder={t("customLicenceWizard.fieldSimulatorsPlaceholder")}
              />
              <p className="text-xs text-gray-500 mt-1">{t("customLicenceWizard.fieldSimulatorsHelp")}</p>
            </div>

            {simulatorIds.length > 0 && (
              <div className="rounded border bg-violet-50 p-3 text-sm">
                <div className="font-medium text-violet-900 mb-1">
                  {t("customLicenceWizard.simulatorsSelectedCount", { count: simulatorIds.length })}
                </div>
                <ul className="list-disc pl-5 text-gray-700">
                  {simulatorIds.map((id) => (
                    <li key={id}>{simulatorLabelMap.get(id) || id}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Attribution + quota mutualisé */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="clw-quantity">{t("customLicenceWizard.fieldQuantity")} *</Label>
                <Input
                  id="clw-quantity"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                />
                <p className="text-xs text-gray-500 mt-1">{t("customLicenceWizard.fieldQuantityHelp")}</p>
              </div>
              <div>
                <Label htmlFor="clw-sessions">{t("customLicenceWizard.fieldSessions")} *</Label>
                <Input
                  id="clw-sessions"
                  type="number"
                  min={1}
                  value={rateLimitSessions}
                  onChange={(e) => setRateLimitSessions(Math.max(1, Number(e.target.value) || 1))}
                />
                <p className="text-xs text-gray-500 mt-1">{t("customLicenceWizard.fieldSessionsHelp")}</p>
              </div>
            </div>

            <div>
              <Label htmlFor="clw-duration">{t("customLicenceWizard.fieldDuration")}</Label>
              <Input
                id="clw-duration"
                type="number"
                min={0}
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
                placeholder={t("customLicenceWizard.fieldDurationPlaceholder")}
              />
              <p className="text-xs text-gray-500 mt-1">{t("customLicenceWizard.fieldDurationHelp")}</p>
            </div>

            <div>
              <Label htmlFor="clw-note">{t("customLicenceWizard.fieldNote")}</Label>
              <Textarea
                id="clw-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("customLicenceWizard.fieldNotePlaceholder")}
                rows={2}
              />
            </div>

            {/* Récap final */}
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm">
              <div className="font-semibold text-violet-900 mb-2">{t("customLicenceWizard.summaryTitle")}</div>
              <div className="text-gray-800 leading-relaxed">
                {t("customLicenceWizard.summaryText", {
                  sessions: rateLimitSessions,
                  users: quantity,
                  simulators: simulatorIds.length,
                  account: selectedAccount?.name || "—",
                })}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-2 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Footer navigation */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t mt-2">
          <Button
            variant="outline"
            onClick={() => (step === 1 ? onClose() : setStep((s) => (s - 1) as Step))}
            disabled={submitting}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 1 ? t("common.cancel") : t("common.back")}
          </Button>

          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => (s + 1) as Step)}
              disabled={step === 1 ? !canGoStep2 : !canGoStep3}
            >
              {t("customLicenceWizard.next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  {t("customLicenceWizard.submitting")}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  {t("customLicenceWizard.submit")}
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
